import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FileText,
  BadgeCheck,
  XCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  ClipboardList,
  Clock,
  CheckCircle,
  Settings,
} from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import LeadChat from "../common/LeadChat";
import { formatMoneyDisplay } from "../../utils/money";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { useUnreadLeads } from "../../hooks/UnreadLeadsContext";
import { SkeletonStack } from "../common/Skeleton";

function statusBadgeType(status) {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s === "accepted") return "success";
  if (s === "declined" || s === "declined by customer") return "error";
  if (s === "quoted") return "info";
  return "default";
}

export default function CustomerDashboard({ session, profile, onSubmitNew, refreshKey = 0, onViewMessages, onViewSettings, messageDeepLink, onDeepLinkConsumed }) {
  const { showToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [leadDetailsMap, setLeadDetailsMap] = useState({}); // lead_id -> { notes, ... }
  const [leadMediaMap, setLeadMediaMap] = useState({});     // lead_id -> [{ file_url, file_name }]
  const [loading, setLoading] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [responding, setResponding] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [msgBannerDismissed, setMsgBannerDismissed] = useState(false);
  const { unreadCount, registerLeadsWithTradieActivity } = useUnreadLeads();
  // Per-quote chat toggle — many quotes per lead, each with its own thread.
  // Starting collapsed keeps the dashboard scannable; clicking "Messages"
  // on a quote card opens that quote's thread in place.
  const [openChatQuoteIds, setOpenChatQuoteIds] = useState(() => new Set());
  const toggleChat = (quoteId) => {
    setOpenChatQuoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
  };

  const firstName = (profile?.full_name || "").split(" ")[0];

  // Detect return from Stripe deposit payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      showToast("Deposit paid. Your job is confirmed!", "success");
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.toString());
    }
  }, [showToast]);

  // Apply the email-link deep link once leads + quotes are loaded so the
  // matching request card opens with its Messages thread visible. We wait
  // until we actually have data for the lead — otherwise the expanded
  // section would render before the quote rows are available, leaving the
  // chat thread empty.
  useEffect(() => {
    if (!messageDeepLink?.leadId) return;
    if (loading) return;
    const leadExists = leads.some((l) => l.id === messageDeepLink.leadId);
    if (!leadExists) return;
    setExpandedLeadId(messageDeepLink.leadId);
    if (messageDeepLink.tab === "messages") {
      const leadQuotes = quotes
        .filter((q) => q.lead_id === messageDeepLink.leadId)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const targetQuote = leadQuotes[0];
      if (targetQuote) {
        setOpenChatQuoteIds((prev) => {
          if (prev.has(targetQuote.id)) return prev;
          const next = new Set(prev);
          next.add(targetQuote.id);
          return next;
        });
      }
    }
    onDeepLinkConsumed?.();
  }, [messageDeepLink, loading, leads, quotes, onDeepLinkConsumed]);

  const loadData = useCallback(async () => {
    if (!session?.user?.id) return;

    setLoading(true);
    try {
      // Fetch leads linked to this user's account
      const { data: leadsData, error: leadsError } = await supabase
        .from("leads")
        .select("id, customer_name, trade, project_type, suburb, status, created_at")
        .eq("customer_user_id", session.user.id)
        .order("created_at", { ascending: false });

      if (leadsError) throw leadsError;

      // Guest quote matching: also fetch leads submitted without an account
      // that share the same email or phone as the logged-in user.
      // SECURITY: This query relies on email/phone match for authorisation.
      // RLS on leads table must restrict this to authenticated users only.
      // Ensure leads RLS has: USING (customer_user_id = auth.uid() OR customer_email = auth.jwt()->>'email')
      // The outer guard (session?.user?.id check at top of loadData) ensures only
      // authenticated users reach this code path.
      const userEmail = session.user.email;
      const userPhone = profile?.phone || "";
      const accountLeadIds = new Set((leadsData || []).map((l) => l.id));
      let guestLeads = [];

      if (userEmail || userPhone) {
        const guestQueries = [];
        if (userEmail) {
          guestQueries.push(
            supabase
              .from("leads")
              .select("id, customer_name, trade, project_type, suburb, status, created_at")
              .is("customer_user_id", null)
              .eq("customer_email", userEmail)
          );
        }
        if (userPhone) {
          guestQueries.push(
            supabase
              .from("leads")
              .select("id, customer_name, trade, project_type, suburb, status, created_at")
              .is("customer_user_id", null)
              .eq("customer_phone", userPhone)
          );
        }
        const results = await Promise.all(guestQueries);
        const seen = new Set();
        for (const { data } of results) {
          for (const lead of data || []) {
            // Deduplicate and exclude leads already linked to the account
            if (!accountLeadIds.has(lead.id) && !seen.has(lead.id)) {
              seen.add(lead.id);
              guestLeads.push(lead);
            }
          }
        }
      }

      // Merge account-linked and guest leads, sorted by created_at desc
      const userLeads = [...(leadsData || []), ...guestLeads].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setLeads(userLeads);

      if (userLeads.length === 0) {
        setQuotes([]);
        return;
      }

      const leadIds = userLeads.map((l) => l.id);
      const { data: quoteData, error: quoteError } = await supabase
        .from("quotes")
        .select(
          "id, lead_id, business_id, total, subtotal, gst, intro_text, line_items, customer_status, status, created_at, public_token, deposit_paid, deposit_amount"
        )
        .in("lead_id", leadIds);

      if (quoteError) throw quoteError;

      // Fetch business names for the quotes
      // businesses table verified to exist in Supabase — stores business id + name.
      const bizIds = [...new Set((quoteData || []).map((q) => q.business_id).filter(Boolean))];
      let bizMap = {};
      if (bizIds.length > 0) {
        const { data: bizData } = await supabase
          .from("businesses")
          .select("id, name")
          .in("id", bizIds);
        (bizData || []).forEach((b) => (bizMap[b.id] = b.name));
      }

      setQuotes(
        (quoteData || []).map((q) => ({
          ...q,
          businessName: bizMap[q.business_id] || "Unknown business",
        }))
      );

      // Fetch lead details (notes, measurements) and media (photos) for display
      // in expanded request cards.
      const [detailsResult, mediaResult] = await Promise.all([
        supabase
          .from("lead_details")
          .select("lead_id, notes, measurements")
          .in("lead_id", leadIds),
        supabase
          .from("lead_media")
          .select("lead_id, file_url, file_name")
          .in("lead_id", leadIds),
      ]);

      const detailsMap = {};
      (detailsResult.data || []).forEach((d) => {
        detailsMap[d.lead_id] = d;
      });
      setLeadDetailsMap(detailsMap);

      const mediaMap = {};
      (mediaResult.data || []).forEach((m) => {
        if (!mediaMap[m.lead_id]) mediaMap[m.lead_id] = [];
        mediaMap[m.lead_id].push(m);
      });
      setLeadMediaMap(mediaMap);
    } catch (err) {
      showToast(err.message || "Failed to load your requests.", "error");
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, session?.user?.email, profile?.phone, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  // Real-time quote updates via Supabase Realtime.
  // We use a ref for loadData to avoid tearing down the channel on every
  // loadData identity change, and a ref for leadIds to filter incoming events
  // without re-subscribing.
  const loadDataRef = React.useRef(loadData);
  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);

  const leadIdsRef = React.useRef(new Set());
  useEffect(() => {
    leadIdsRef.current = new Set(leads.map((l) => l.id));
  }, [leads]);

  // Realtime subscription with auto-reconnect + backoff.
  //
  // Quirks we handle:
  //   • A single CHANNEL_ERROR on a transient websocket blip (wifi switch,
  //     brief drop) would previously trigger the "keep dropping" banner
  //     after only a handful of retries. We now tolerate more blips before
  //     alerting the user (threshold bumped from 4 to 5).
  //   • Device sleep / tab backgrounding naturally tears the socket. When
  //     the tab becomes visible again we reset the retry counter and
  //     force a reconnect + refetch, so a sleep-wake cycle can never push
  //     the user over the banner threshold on its own.
  //   • Every successful (re)connect refetches once so nothing that
  //     arrived while offline is missed.
  //
  // Backoff: 1s → 2s → 4s → 8s → 16s capped at 30s.
  // Banner: shown only after 5+ consecutive failed reconnect attempts
  //         AND only if the document is currently visible (so we don't
  //         flash an error banner at a user returning to a sleeping tab).
  useEffect(() => {
    let channel = null;
    let retryCount = 0;
    let retryTimer = null;
    let toastShown = false;
    let cancelled = false;

    function scheduleReconnect() {
      if (cancelled) return;
      retryCount += 1;
      const delay = Math.min(1000 * 2 ** Math.max(0, retryCount - 1), 30000);
      retryTimer = setTimeout(() => {
        if (cancelled) return;
        connect();
      }, delay);

      // Only alert once the channel has failed to recover across several
      // backoff windows AND the tab is actually in the foreground. A user
      // staring at the dashboard has a legitimate reason to see this;
      // a user returning from sleep does not.
      if (
        retryCount >= 5 &&
        !toastShown &&
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        toastShown = true;
        showToast(
          "Live quote updates keep dropping — refresh if your quotes look stale.",
          "error"
        );
      }
    }

    function connect() {
      // Clean up any previous channel before we attempt a new one so we
      // never stack duplicate subscriptions on retries.
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }

      channel = supabase
        .channel(`customer-quotes-realtime-${Date.now()}`)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "quotes" },
          (payload) => {
            if (payload.new && leadIdsRef.current.has(payload.new.lead_id)) {
              loadDataRef.current();
            }
          }
        )
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "quotes" },
          (payload) => {
            if (payload.new && leadIdsRef.current.has(payload.new.lead_id)) {
              loadDataRef.current();
            }
          }
        )
        .on(
          // Leads-INSERT — covers the "I just submitted a request and the
          // dashboard doesn't show it" race. RLS (leads_select_own) already
          // scopes the events to leads this user owns, so we can refetch
          // unconditionally on any event we actually receive.
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "leads" },
          () => {
            loadDataRef.current();
          }
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            // Back online — reset retry state and refetch so the user
            // sees anything that arrived while the channel was down.
            retryCount = 0;
            toastShown = false;
            loadDataRef.current();
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
            scheduleReconnect();
          }
        });
    }

    // Tab wake-up: reset backoff and force a fresh connect so a phone that
    // was asleep for 20 minutes rejoins instantly instead of crawling up
    // its own backoff chain.
    function handleVisibility() {
      if (document.visibilityState !== "visible") return;
      if (cancelled) return;
      retryCount = 0;
      toastShown = false;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      connect();
    }

    document.addEventListener("visibilitychange", handleVisibility);

    connect();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (retryTimer) clearTimeout(retryTimer);
      if (channel) supabase.removeChannel(channel);
    };
    // Subscribe once on mount, clean up on unmount. Refs and showToast are
    // provider-stable, so no deps needed. eslint-disable is intentional.
    // eslint-disable-next-line
  }, []);

  // Identify leads with tradie activity and register them with the unread
  // provider. The provider subtracts any leads the user has already opened
  // (per useReadMessages' localStorage), so the banner/badge counts decrement
  // as soon as markLeadRead fires from CustomerMessages.
  //
  // Scope "tradie activity" to messages created after the customer's auth
  // account: lead_messages has no is_read column, and merging guest leads by
  // email pulls in older tradie replies that shouldn't count as new for this
  // freshly-authed user.
  useEffect(() => {
    if (leads.length === 0) {
      registerLeadsWithTradieActivity([]);
      return;
    }
    const ids = leads.map((l) => l.id);
    const accountCreatedAt = session?.user?.created_at;
    let q = supabase
      .from("lead_messages")
      .select("lead_id")
      .eq("sender_role", "tradie")
      .in("lead_id", ids);
    if (accountCreatedAt) q = q.gt("created_at", accountCreatedAt);
    q.then(({ data }) => {
      const uniq = Array.from(new Set((data || []).map((r) => r.lead_id)));
      registerLeadsWithTradieActivity(uniq);
    });
  }, [leads, session?.user?.created_at, registerLeadsWithTradieActivity]);

  // Stats
  const stats = useMemo(() => {
    const total = leads.length;
    const awaiting = leads.filter((l) => {
      const s = (l.status || "").toLowerCase();
      return s === "" || s === "open" || s === "quoted";
    }).length;
    const accepted = leads.filter(
      (l) => (l.status || "").toLowerCase() === "accepted"
    ).length;
    return { total, awaiting, accepted };
  }, [leads]);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    if (activeFilter === "active") {
      return leads.filter((l) => {
        const s = (l.status || "").toLowerCase();
        return s === "" || s === "open" || s === "quoted";
      });
    }
    if (activeFilter === "completed") {
      return leads.filter((l) => {
        const s = (l.status || "").toLowerCase();
        return s === "accepted" || s === "declined" || s === "declined by customer";
      });
    }
    return leads;
  }, [leads, activeFilter]);

  // Fire-and-forget — never blocks the user. Edge function is deployed
  // separately (send-quote-decision-email).
  function dispatchDecisionEmail(quoteId, outcome) {
    supabase.functions
      .invoke("send-quote-decision-email", {
        body: { quoteId, outcome, appUrl: window.location.origin },
      })
      .then(({ error }) => {
        if (error) {
          // eslint-disable-next-line no-console
          console.warn("[send-quote-decision-email] invoke error:", error);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[send-quote-decision-email] network error:", err);
      });
  }

  // Recompute the lead-level status from the latest quote states. Called
  // after every decline/accept/undo so a single write site handles the
  // rollup rather than scattering string literals across branches.
  //
  //   • any accepted quote  → "accepted"
  //   • all declined        → "declined"
  //   • any pending/sent    → "quoted" (at least one quote exists)
  //   • no quotes           → "open"
  async function rollupLeadStatus(leadId) {
    const { data: qs } = await supabase
      .from("quotes")
      .select("id, customer_status")
      .eq("lead_id", leadId);

    const rows = qs || [];
    let nextStatus;
    let acceptedQuoteId = null;
    let winningBusinessId = null;

    if (rows.length === 0) {
      nextStatus = "open";
    } else if (rows.some((q) => q.customer_status === "accepted")) {
      nextStatus = "accepted";
      const acc = rows.find((q) => q.customer_status === "accepted");
      acceptedQuoteId = acc?.id || null;
      // business_id isn't on this select — look it up from the in-memory list.
      const accFull = quotes.find((q) => q.id === acceptedQuoteId);
      winningBusinessId = accFull?.business_id || null;
    } else if (rows.every((q) => q.customer_status === "declined")) {
      nextStatus = "declined";
    } else {
      nextStatus = "quoted";
    }

    const patch = { status: nextStatus };
    if (nextStatus === "accepted") {
      patch.accepted_quote_id = acceptedQuoteId;
      patch.winning_business_id = winningBusinessId;
    } else {
      // Clear accept-only fields so an undo genuinely reverts the lead
      // to its pre-accept state.
      patch.accepted_quote_id = null;
      patch.winning_business_id = null;
    }

    await supabase.from("leads").update(patch).eq("id", leadId);
  }

  async function handleResponse(quoteId, leadId, status) {
    setResponding(true);
    try {
      const now = new Date().toISOString();

      // 1. Write the decision onto the chosen quote.
      const { error: quoteErr } = await supabase
        .from("quotes")
        .update({
          customer_status: status,
          customer_responded_at: now,
          status: status === "accepted" ? "accepted" : "declined",
        })
        .eq("id", quoteId);
      if (quoteErr) throw quoteErr;

      if (status === "accepted") {
        // 2a. Auto-decline every sibling quote on the same lead.
        const siblingIds = quotes
          .filter((q) => q.lead_id === leadId && q.id !== quoteId)
          .map((q) => q.id);

        if (siblingIds.length > 0) {
          await supabase
            .from("quotes")
            .update({
              customer_status: "declined",
              customer_responded_at: now,
              status: "declined",
            })
            .in("id", siblingIds);
        }

        // 3a. Notify the winner + all losers. Fire-and-forget; deposit
        // redirect shouldn't wait on email.
        dispatchDecisionEmail(quoteId, "accepted");
        siblingIds.forEach((sid) => dispatchDecisionEmail(sid, "auto-declined"));

        // 4a. Roll the lead status up from the full quote set.
        await rollupLeadStatus(leadId);

        // 5a. Kick off the deposit payment session and redirect if possible.
        const acceptedQuote = quotes.find((q) => q.id === quoteId);
        const returnUrl = `${window.location.origin}${window.location.pathname}?payment=success`;
        const { data: checkoutData, error: checkoutErr } =
          await supabase.functions.invoke("create-deposit-session", {
            body: {
              quoteId,
              businessId: acceptedQuote?.business_id || null,
              customerId: session?.user?.id || null,
              returnUrl,
            },
          });

        if (!checkoutErr && checkoutData?.url) {
          window.location.href = checkoutData.url;
          return;
        }

        // Deposit session failed — acceptance still stuck. Surface as an
        // error-typed toast so the customer acts on it.
        showToast(
          "Quote accepted — but deposit payment could not be started. Please contact the business.",
          "error"
        );
        await loadData();
      } else {
        // 2b. Decline path: only this one quote changes. The lead status
        // is rolled up from the complete picture so a decline doesn't
        // prematurely mark the lead declined when other quotes are pending.
        dispatchDecisionEmail(quoteId, "declined");
        await rollupLeadStatus(leadId);
        showToast("Quote declined. You can undo from this card.", "info");
        await loadData();
      }
    } catch (err) {
      showToast(err.message || "Failed to submit response.", "error");
    } finally {
      setResponding(false);
    }
  }

  // Silent reinstatement — used from the "Undo" action on a declined quote
  // card. No email is sent (by design: the tradie never needs to know a
  // momentary decline happened). Quote goes back to pending, lead status
  // is recomputed so a previously-"declined" lead becomes "quoted" again.
  async function handleUndoDecline(quoteId, leadId) {
    setResponding(true);
    try {
      const { error } = await supabase
        .from("quotes")
        .update({
          customer_status: null,
          customer_responded_at: null,
          status: "sent",
        })
        .eq("id", quoteId);
      if (error) throw error;

      await rollupLeadStatus(leadId);
      showToast("Decline reversed — the quote is active again.", "success");
      await loadData();
    } catch (err) {
      showToast(err.message || "Failed to undo decline.", "error");
    } finally {
      setResponding(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <SkeletonStack lines={4} widths={["55%", "100%", "85%", "70%"]} />
      </Card>
    );
  }

  return (
    <div className="page customer-shell">
      {/* Unread messages banner */}
      {unreadCount > 0 && !msgBannerDismissed && onViewMessages && (
        <div style={{
          background: "var(--surface-2)",
          border: "1px solid var(--hairline)",
          borderLeft: "3px solid var(--primary, #FF8A00)",
          borderRadius: "12px",
          padding: "14px 18px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}>
          <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>💬</span>
          <span style={{ flex: 1, fontSize: "0.95rem", color: "var(--text, #1C1C1E)", minWidth: "140px" }}>
            You have <strong>{unreadCount}</strong> lead{unreadCount === 1 ? "" : "s"} with new messages from your tradie{unreadCount === 1 ? "" : "s"}.
          </span>
          <button
            type="button"
            onClick={onViewMessages}
            style={{
              background: "var(--primary)", color: "#fff", border: "none",
              borderRadius: "8px", padding: "7px 14px", fontSize: "0.85rem",
              fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            View messages →
          </button>
          <button
            type="button"
            onClick={() => setMsgBannerDismissed(true)}
            aria-label="Dismiss"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-muted, #6b7280)", fontSize: "1.3rem", lineHeight: 1,
              padding: "2px", flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Greeting */}
      {firstName && (
        <p className="muted" style={{ marginBottom: "0.25rem" }}>
          Welcome back, {firstName}
        </p>
      )}

      <div className="two-grid" style={{ marginBottom: "1.5rem" }}>
        <Card className="hero dark">
          <h1>Your quote requests</h1>
          <p>
            Track your submitted jobs and review quotes from tradies. Accept or
            decline directly from here.
          </p>
        </Card>

        <Card>
          <h3>Submit a new request</h3>
          <p className="muted">
            Need another job quoted? Start a new request and get quotes from
            local tradies.
          </p>
          <div className="button-row">
            <Button onClick={onSubmitNew}>
              <Plus size={16} /> New request
            </Button>
            <Button secondary onClick={() => setShowHowItWorks((v) => !v)}>
              <HelpCircle size={16} /> How it works
            </Button>
            {onViewSettings && (
              <Button secondary onClick={onViewSettings}>
                <Settings size={16} /> Settings
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* How it works explainer (toggled) */}
      {showHowItWorks && (
        <Card style={{ marginBottom: "1.5rem" }}>
          <h3>How it works</h3>
          <div className="three-grid">
            <div>
              <h4>1. Describe your job</h4>
              <p className="muted">Tell us what you need done — the trade, location, and project details.</p>
            </div>
            <div>
              <h4>2. Receive quotes from tradies</h4>
              <p className="muted">Local tradies review your request and send you competitive quotes.</p>
            </div>
            <div>
              <h4>3. Accept the best fit</h4>
              <p className="muted">Compare quotes, choose the right tradie, and get your job started.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Filter tabs */}
      <div className="button-row" style={{ marginBottom: "1rem" }}>
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "completed", label: "Completed" },
        ].map((tab) => (
          <Button
            key={tab.key}
            secondary={activeFilter !== tab.key}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {leads.length === 0 ? (
        <Card>
          <h3>No requests yet</h3>
          <p className="muted">
            You haven't submitted any quote requests yet. Get started by describing
            your job — it only takes a couple of minutes.
          </p>
          <div className="button-row" style={{ marginTop: "1rem" }}>
            <Button onClick={onSubmitNew}>
              <Plus size={16} /> New request
            </Button>
          </div>
          <div className="three-grid" style={{ marginTop: "1.5rem" }}>
            <div>
              <h4>1. Describe your job</h4>
              <p className="muted">Tell us what you need done — the trade, location, and project details.</p>
            </div>
            <div>
              <h4>2. Receive quotes from tradies</h4>
              <p className="muted">Local tradies review your request and send you competitive quotes.</p>
            </div>
            <div>
              <h4>3. Accept the best fit</h4>
              <p className="muted">Compare quotes, choose the right tradie, and get your job started.</p>
            </div>
          </div>
        </Card>
      ) : filteredLeads.length === 0 ? (
        <Card>
          <p className="muted">No {activeFilter} requests to show.</p>
        </Card>
      ) : (
        <div className="lead-list">
          {filteredLeads.map((lead) => {
            const leadQuotes = quotes.filter((q) => q.lead_id === lead.id);
            const isExpanded = expandedLeadId === lead.id;

            return (
              <div key={lead.id} className="lead-card">
                <div
                  className="lead-top"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setExpandedLeadId(isExpanded ? null : lead.id)
                  }
                >
                  <div>
                    <div className="lead-name">
                      {lead.trade} — {lead.project_type || "Quote request"}
                    </div>
                    <div className="muted small">
                      {lead.suburb} &bull;{" "}
                      {new Date(lead.created_at).toLocaleDateString("en-AU")}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Badge type={statusBadgeType(lead.status)}>
                      {lead.status || "open"}
                    </Badge>
                    {leadQuotes.length > 0 && (
                      <Badge type="info">{leadQuotes.length} quote{leadQuotes.length !== 1 ? "s" : ""}</Badge>
                    )}
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: "1rem" }}>
                    {/* Photos submitted with this request */}
                    {leadMediaMap[lead.id] && leadMediaMap[lead.id].length > 0 && (
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                        {leadMediaMap[lead.id].map((media, i) => (
                          <img
                            key={i}
                            src={media.file_url}
                            alt={media.file_name || `Photo ${i + 1}`}
                            style={{
                              width: 64,
                              height: 64,
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Notes submitted with this request */}
                    {leadDetailsMap[lead.id]?.notes && (
                      <p className="muted" style={{ marginBottom: "0.75rem", whiteSpace: "pre-wrap" }}>
                        <strong>Notes:</strong> {leadDetailsMap[lead.id].notes}
                      </p>
                    )}

                    {leadQuotes.length === 0 ? (
                      <p className="muted">No quotes received yet.</p>
                    ) : (
                      <>
                        {/* Lead-level context:
                            • If a quote has been accepted the top of the
                              expanded region gets a clear banner so the
                              customer sees the outcome before scanning cards.
                            • If EVERY received quote has been declined the
                              banner prompts the customer to either undo one
                              of the declines or submit a new request. */}
                        {(() => {
                          const hasAccepted = leadQuotes.some((q) => q.customer_status === "accepted");
                          const allDeclined = leadQuotes.length > 0 && leadQuotes.every((q) => q.customer_status === "declined");
                          if (hasAccepted) return null;
                          if (!allDeclined) return null;
                          return (
                            <div style={{
                              background: "var(--surface-2)",
                              border: "1px solid var(--hairline)",
                              borderLeft: "3px solid var(--primary, #FF8A00)",
                              borderRadius: "12px",
                              padding: "12px 14px",
                              marginBottom: "12px",
                              color: "var(--text, #1C1C1E)",
                              fontSize: "0.9rem",
                            }}>
                              You've declined all quotes. You can undo any decline below, or{" "}
                              <button
                                type="button"
                                onClick={onSubmitNew}
                                style={{
                                  background: "none", border: "none", padding: 0,
                                  color: "var(--primary)", fontWeight: 600, cursor: "pointer",
                                  textDecoration: "underline", fontSize: "inherit",
                                }}
                              >
                                submit a new request
                              </button>.
                            </div>
                          );
                        })()}

                        <div className="review-grid">
                          {leadQuotes.map((quote) => {
                            const lineItems = Array.isArray(quote.line_items)
                              ? quote.line_items
                              : [];

                            // Sibling-aware state. Every locked/read-only
                            // variant derives from the full quote set on
                            // this lead, not just the current quote row —
                            // so undoing a decline or accepting elsewhere
                            // cleanly flips what each card shows.
                            const siblings = leadQuotes;
                            const acceptedSibling = siblings.find(
                              (q) => q.customer_status === "accepted"
                            );
                            const someoneWon = !!acceptedSibling;
                            const isAccepted = quote.customer_status === "accepted";
                            const isDeclined = quote.customer_status === "declined";
                            const isAwardedElsewhere = !isAccepted && someoneWon;
                            const isPending = !isAccepted && !isDeclined && !someoneWon;

                            // Chat activity rules (per spec):
                            //   • Accepted tradie's chat stays active
                            //   • Everyone else becomes read-only once a
                            //     sibling has been accepted or the customer
                            //     has declined this specific quote
                            //   • Undo-ing the decline (while no sibling
                            //     is accepted) flips the thread active
                            //     again automatically.
                            const chatReadOnly = isDeclined || isAwardedElsewhere;
                            const chatOpen = openChatQuoteIds.has(quote.id);
                            const chatReadOnlyReason = isAwardedElsewhere
                              ? "Job awarded to another tradie — messages are read-only."
                              : isDeclined
                                ? "You declined this quote — messages are read-only. Undo to reopen."
                                : null;

                            // Dim the card when it isn't the "live" one.
                            // Uses existing surface tokens only — no new
                            // CSS classes.
                            const cardStyle = (isDeclined || isAwardedElsewhere)
                              ? { opacity: 0.7, background: "var(--surface-3, var(--surface-2))" }
                              : undefined;

                            return (
                              <Card key={quote.id} style={cardStyle}>
                                <h4 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                  <FileText size={16} />
                                  {quote.businessName}
                                </h4>

                                <div className="estimate-box">
                                  <div className="muted small">Total</div>
                                  <div className="estimate-range">
                                    <strong>{formatMoneyDisplay(quote.total || 0)}</strong>
                                  </div>
                                </div>

                                {lineItems.length > 0 && (
                                  <div style={{ marginBottom: "0.75rem" }}>
                                    {lineItems.map((item, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          padding: "0.25rem 0",
                                          borderBottom: "1px solid var(--border)",
                                        }}
                                      >
                                        <span className="muted small">
                                          {item.description || "Item"}
                                        </span>
                                        <span className="feature-title">
                                          {formatMoneyDisplay(item.amount || 0)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {quote.intro_text && (
                                  <p className="muted" style={{ fontSize: "0.85rem" }}>
                                    {quote.intro_text}
                                  </p>
                                )}

                                {/* Status badges — sibling-aware so the
                                    "loser" quotes after an accept show
                                    "Job awarded" rather than a plain
                                    "Declined" (which would misrepresent
                                    the customer's actual action). */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", margin: "0.25rem 0" }}>
                                  {isAccepted && <Badge type="success">Accepted</Badge>}
                                  {quote.deposit_paid && (
                                    <Badge type="success">
                                      Deposit paid{quote.deposit_amount ? ` — ${formatMoneyDisplay(quote.deposit_amount)}` : ""}
                                    </Badge>
                                  )}
                                  {isAwardedElsewhere && <Badge type="default">Job awarded</Badge>}
                                  {isDeclined && !isAwardedElsewhere && <Badge type="error">Declined</Badge>}
                                </div>

                                {/* Primary actions. "Accept" is the loud
                                    orange primary Button (default variant);
                                    "Decline" is the quieter secondary. */}
                                {isPending && (
                                  <div className="button-row" style={{ marginTop: "0.75rem" }}>
                                    <Button
                                      onClick={() =>
                                        handleResponse(quote.id, lead.id, "accepted")
                                      }
                                      disabled={responding}
                                    >
                                      <BadgeCheck size={14} /> Accept
                                    </Button>
                                    <Button
                                      secondary
                                      onClick={() =>
                                        handleResponse(quote.id, lead.id, "declined")
                                      }
                                      disabled={responding}
                                    >
                                      <XCircle size={14} /> Decline
                                    </Button>
                                  </div>
                                )}

                                {/* Undo lives only on cards declined by the
                                    customer — auto-declined siblings should
                                    not reappear via an undo on a loser card
                                    (the accept is the source of truth). */}
                                {isDeclined && !isAwardedElsewhere && (
                                  <div className="button-row" style={{ marginTop: "0.75rem" }}>
                                    <Button
                                      secondary
                                      onClick={() => handleUndoDecline(quote.id, lead.id)}
                                      disabled={responding}
                                    >
                                      Undo decline
                                    </Button>
                                  </div>
                                )}

                                {/* Per-quote messages thread. Always mounted
                                    once the user opens it so the LeadChat
                                    subscription isn't cycled on re-render. */}
                                <div style={{ marginTop: "0.75rem", borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                                  <button
                                    type="button"
                                    onClick={() => toggleChat(quote.id)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      color: "var(--primary)",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {chatOpen ? "Hide messages ↑" : "Messages ↓"}
                                  </button>
                                  {chatOpen && (
                                    <div style={{ marginTop: "0.5rem" }}>
                                      <LeadChat
                                        leadId={lead.id}
                                        senderRole="customer"
                                        senderName={(profile?.full_name || lead.customer_name || "Customer").split(" ")[0]}
                                        businessId={quote.business_id}
                                        readOnly={chatReadOnly}
                                        readOnlyReason={chatReadOnlyReason}
                                      />
                                    </div>
                                  )}
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Stats row */}
      <div className="three-grid" style={{ marginBottom: "1.5rem" }}>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <ClipboardList size={18} />
            <span className="muted">Total requests</span>
          </div>
          <h2 style={{ margin: "0.25rem 0 0" }}>{stats.total}</h2>
        </Card>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Clock size={18} />
            <span className="muted">Awaiting quotes</span>
          </div>
          <h2 style={{ margin: "0.25rem 0 0" }}>{stats.awaiting}</h2>
        </Card>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <CheckCircle size={18} />
            <span className="muted">Jobs accepted</span>
          </div>
          <h2 style={{ margin: "0.25rem 0 0" }}>{stats.accepted}</h2>
        </Card>
      </div>
    </div>
  );
}
